import React, { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Check, Loader2, Crown, Users, Building2, Sparkles } from "lucide-react";
import { KLARTEXT_PLANS, PLAN_ORDER, formatPrice } from "@/lib/klartext-pricing";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface PricingSectionProps {
  currentPlan: string;
  isAuthenticated: boolean;
  onLoginRequired: () => void;
}

const planIcons: Record<string, React.ReactNode> = {
  free: <Sparkles className="h-6 w-6" />,
  familie: <Users className="h-6 w-6" />,
  pro: <Crown className="h-6 w-6" />,
  firma: <Building2 className="h-6 w-6" />,
};

export function PricingSection({ currentPlan, isAuthenticated, onLoginRequired }: PricingSectionProps) {
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubscribe = async (planId: string) => {
    const plan = KLARTEXT_PLANS[planId];
    if (!plan || !plan.priceId) return;

    if (!isAuthenticated) {
      onLoginRequired();
      return;
    }

    setLoadingPlan(planId);

    try {
      const { data, error } = await supabase.functions.invoke('klartext-create-checkout', {
        body: { plan: planId },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast({
        title: 'Fehler',
        description: 'Checkout konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
        variant: 'destructive',
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="pricing" className="py-16 bg-gradient-to-b from-white to-slate-50">
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Wählen Sie Ihren Schutz
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Von kostenlos bis Enterprise – wählen Sie den Plan, der zu Ihnen passt.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLAN_ORDER.map((planId) => {
            const plan = KLARTEXT_PLANS[planId];
            const isCurrentPlan = currentPlan === planId;
            const isPaid = plan.priceMonthly > 0;

            return (
              <Card 
                key={planId}
                className={`relative flex flex-col transition-all duration-300 hover:shadow-xl ${
                  plan.highlight 
                    ? 'border-2 border-primary shadow-lg ring-2 ring-primary/20' 
                    : 'border border-slate-200'
                } ${isCurrentPlan ? 'bg-primary/5' : ''}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-primary to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <CardHeader className="text-center pb-4">
                  <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                    plan.highlight 
                      ? 'bg-gradient-to-r from-primary to-blue-600 text-white' 
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {planIcons[planId]}
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold text-slate-900">
                      {plan.priceMonthly === 0 ? 'Gratis' : formatPrice(plan.priceMonthly)}
                    </span>
                    {plan.priceMonthly > 0 && (
                      <span className="text-slate-500 text-sm">/Monat</span>
                    )}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col">
                  <ul className="space-y-3 mb-6 flex-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlan ? (
                    <Button disabled className="w-full rounded-xl" variant="outline">
                      Aktueller Plan
                    </Button>
                  ) : isPaid ? (
                    <Button
                      onClick={() => handleSubscribe(planId)}
                      disabled={loadingPlan === planId}
                      className={`w-full rounded-xl ${
                        plan.highlight
                          ? 'bg-gradient-to-r from-primary to-blue-600 text-white hover:opacity-90'
                          : ''
                      }`}
                      variant={plan.highlight ? 'default' : 'outline'}
                    >
                      {loadingPlan === planId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Jetzt upgraden'
                      )}
                    </Button>
                  ) : (
                    <Button disabled className="w-full rounded-xl" variant="ghost">
                      Bereits aktiv
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-sm text-slate-500 mt-8">
          Alle Preise inkl. MwSt. • Monatlich kündbar • Sichere Zahlung via Stripe
        </p>
      </div>
    </section>
  );
}
